// Brand picker for a brand_manager invitation (ADR-0164), and for a
// supervisor's brand of affiliation (ADR-0165) with its own label and
// hint. Shared by the platform-admin org panel and the self-service Equipo
// screen. No preselection: the inviter always picks the brand on purpose.

import { forwardRef } from "react";

export type InviteBrandFieldProps = {
  brands: { id: string; name: string }[];
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  idPrefix: string;
  /** Defaults to «Marca». */
  label?: string;
  /** Defaults to the brand manager's hint (read-only view of one brand). */
  hint?: string;
};

const BRAND_MANAGER_HINT =
  "La persona solo verá las tiendas, observaciones y reportes de esta marca, sin poder modificarlos.";

export const InviteBrandField = forwardRef<HTMLSelectElement, InviteBrandFieldProps>(
  function InviteBrandField(
    { brands, value, onChange, error, idPrefix, label = "Marca", hint = BRAND_MANAGER_HINT },
    ref
  ) {
    const selectId = `${idPrefix}-brand`;
    const hintId = `${idPrefix}-brand-hint`;
    const errorId = `${idPrefix}-brand-error`;

    if (brands.length === 0) {
      return (
        <p className="cell-meta" role="status">
          Esta organización aún no tiene marcas. Crea una antes de invitar a un gerente de marca.
        </p>
      );
    }

    return (
      <div className="field">
        <label className="field__label" htmlFor={selectId}>
          {label}
        </label>
        <select
          ref={ref}
          id={selectId}
          className="input"
          required
          aria-required="true"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${hintId} ${errorId}` : hintId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Elige una marca</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <span id={hintId} className="field__hint">
          {hint}
        </span>
        {error && (
          <span id={errorId} className="auth-error" role="alert">
            {error}
          </span>
        )}
      </div>
    );
  }
);
